"use client";
import * as React from "react";
import {
  CarouselApi,
  CarouselContent,
  CarouselItem,
  useCarousel,
} from "@/components/ui/carousel";
import MobileCard from "./MobileCard";
import { useWindowSize } from "@/hooks/use-window-size";
import { EmblaEventType } from "embla-carousel";
import { useCallback, useEffect, useRef, useState } from "react";
import { AwardCategoryProps } from "@/services/home/types";

type MobileCarouselProps = {
  data: AwardCategoryProps[];
};

const TWEEN_FACTOR_BASE = 0.84;

const numberWithinRange = (number: number, min: number, max: number): number =>
  Math.min(Math.max(number, min), max);

export function MobileCarousel({ data }: MobileCarouselProps) {
  const { api: emblaApi } = useCarousel();
  const tweenFactor = useRef(0);
  const { width } = useWindowSize();
  const [itemsInView, setItemsInView] = useState(3);
  const setTweenFactor = useCallback((emblaApi: CarouselApi) => {
    tweenFactor.current = TWEEN_FACTOR_BASE * emblaApi!.scrollSnapList().length;
  }, []);

  const tweenOpacity = useCallback(
    (emblaApi: CarouselApi, eventName?: EmblaEventType) => {
      if (!emblaApi) return;

      const engine = emblaApi.internalEngine();
      const scrollProgress = emblaApi.scrollProgress();
      const slidesInView = emblaApi.slidesInView();
      const isScrollEvent = eventName === "scroll";

      emblaApi.scrollSnapList().forEach((scrollSnap, snapIndex) => {
        let diffToTarget = scrollSnap - scrollProgress;
        const slidesInSnap = engine.slideRegistry[snapIndex];

        slidesInSnap.forEach((slideIndex) => {
          if (isScrollEvent && !slidesInView.includes(slideIndex)) return;

          if (engine.options.loop) {
            engine.slideLooper.loopPoints.forEach((loopItem) => {
              const target = loopItem.target();

              if (slideIndex === loopItem.index && target !== 0) {
                const sign = Math.sign(target);

                if (sign === -1) {
                  diffToTarget = scrollSnap - (1 + scrollProgress);
                }
                if (sign === 1) {
                  diffToTarget = scrollSnap + (1 - scrollProgress);
                }
              }
            });
          }

          const tweenValue =
            itemsInView - Math.abs(diffToTarget * tweenFactor.current);
          const opacity = numberWithinRange(tweenValue, 0, 1).toString();
          emblaApi.slideNodes()[slideIndex].style.opacity = opacity;
        });
      });
    },
    [itemsInView],
  );

  useEffect(() => {
    if (width < 1024) {
      setItemsInView(1);
      return;
    }

    if (width < 1280) {
      setItemsInView(2);
      return;
    }

    setItemsInView(3);
  }, [width]);

  useEffect(() => {
    if (!emblaApi) return;

    setTweenFactor(emblaApi);
    tweenOpacity(emblaApi);
    emblaApi
      .on("reInit", setTweenFactor)
      .on("reInit", tweenOpacity)
      .on("scroll", tweenOpacity)
      .on("slideFocus", tweenOpacity);
  }, [emblaApi, tweenOpacity]);

  return (
    <CarouselContent>
      {data?.map((data, index) => {
        return (
          <CarouselItem
            key={index}
            className="embla__slide sm:basis-8/12 lg:basis-5/12 xl:basis-1/4"
          >
            <MobileCard
              description={data?.description ?? ""}
              title={data?.title}
            />
          </CarouselItem>
        );
      })}
    </CarouselContent>
  );
}
